import { PROJECT_STAGE_DEFINITIONS } from './project-stages.js';
import {
  CANONICAL_STAGE_VALUES,
  type CanonicalStage,
  STAGE_RANK_BY_VALUE,
  stageRank,
} from './signal-type-registry.js';

export interface LifecycleRailRung {
  stage: CanonicalStage;
  label: string;
  rank: number;
  /** rank <= the current stage's rank. */
  reached: boolean;
  /** reached AND a direct signal set this rung. */
  evidenced: boolean;
  /** rank === the current stage's rank. */
  current: boolean;
}

const LABEL_BY_STAGE: Record<CanonicalStage, string> = Object.fromEntries(
  PROJECT_STAGE_DEFINITIONS.map((d) => [d.value, d.label]),
) as Record<CanonicalStage, string>;

/**
 * Pure UI contract (design §5): a rung at or below the current stage is reached.
 * A reached rung with a direct signal renders evidenced; a reached rung with no
 * direct signal renders implied. Not stored; derived per-render from the stage
 * and the set of stages that have a direct signal.
 */
export function buildLifecycleRail(
  stage: CanonicalStage | null,
  evidencedStages: ReadonlySet<CanonicalStage>,
): LifecycleRailRung[] {
  const currentRank = stageRank(stage);
  return CANONICAL_STAGE_VALUES.map((value) => {
    const rank = STAGE_RANK_BY_VALUE[value];
    const reached = rank <= currentRank && currentRank > 0;
    return {
      stage: value,
      label: LABEL_BY_STAGE[value],
      rank,
      reached,
      evidenced: reached && evidencedStages.has(value),
      current: rank === currentRank && currentRank > 0,
    };
  });
}
